import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LPProfileCard from 'features/IRWorkstation/components/LPProfileCard';
import LPContactsCard from 'features/IRWorkstation/components/LP360/LPContactsCard';
import LPDescriptionCard from 'features/IRWorkstation/components/LP360/LPDescriptionCard';
import InvestorCommitmentsCard from 'features/IRWorkstation/components/LP360/InvestorCommitmentsCard';
import LPFundraisingsCard from 'features/IRWorkstation/components/LP360/LPFundraisingsCard';
import LP360KPIs from 'features/IRWorkstation/components/LP360/LP360KPIs';
import LPRecentInteractionsCard from 'features/IRWorkstation/components/LP360/LPRecentInteractionsCard';
import LPActivityOverviewCard from 'features/IRWorkstation/components/LP360/LPActivityOverviewCard';
import LPHealthScores from 'features/IRWorkstation/components/LP360/LPHealthScore';
import DraftOutreachDrawer from 'features/IRWorkstation/components/LP360/DraftOutreachDrawer';
import PrimaryButton from 'features/IRWorkstation/components/Common/Buttons/PrimaryButton';
import useAccount from 'features/IRWorkstation/hooks/useAccount';
import useAccountContacts from 'features/IRWorkstation/hooks/useAccountContacts';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';

// The LP360 page shell: a back-link breadcrumb to wherever the user came from
// (only when it arrived via router state, e.g. from a Raise Dashboard) and the
// LP's name as the title. The LP360 components each land here in their own
// tickets under the SS-3730 epic.
const LP360 = () => {
	const { accountId } = useParams();
	const navigate = useNavigate();
	const { state } = useLocation();
	const from = state?.from;
	const { account, loading, error } = useAccount(accountId);
	const { tenant, featureFlags } = useIRWorkstation();
	const { trackButtonClick } = useTrackEvent({ tenant });
	const [draftOutreachOpen, setDraftOutreachOpen] = useState(false);
	const {
		contacts,
		loading: contactsLoading,
		loadingMore: contactsLoadingMore,
		hasMore: contactsHaveMore,
		error: contactsError,
		loadMore: loadMoreContacts,
	} = useAccountContacts(accountId);

	const isDraftOutreachEnabled =
		featureFlags?.['enable-draft-outreach-prompt'] !== false;

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			{from?.pathname && (
				<Box>
					<Button
						startIcon={
							<ArrowBackIcon
								sx={{ fontSize: '16px', width: '16px', height: '16px' }}
							/>
						}
						onClick={() => navigate(from.pathname)}
						sx={{
							color: 'text.secondary',
							fontSize: '14px',
							fontWeight: 500,
							pl: 0,
							justifyContent: 'flex-start',
							'&:hover': {
								backgroundColor: 'transparent',
								color: 'text.primary',
							},
						}}
					>
						{from.label || 'Back'}
					</Button>
				</Box>
			)}

			{/* Header */}
			<Box
				data-testid='lp360-header'
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '14px',
				}}
			>
				{loading ? (
					<Skeleton
						variant='text'
						width={240}
						height={26}
						data-testid='lp360-title-skeleton'
					/>
				) : error ? (
					<Typography color='error'>Failed to load LP details</Typography>
				) : (
					<Typography variant='h1' sx={{ fontSize: '17px', fontWeight: 700 }}>
						{account?.name || 'LP'}
					</Typography>
				)}

				{account && isDraftOutreachEnabled && (
					<PrimaryButton
						data-testid='lp360-draft-outreach-button'
						label='Draft Outreach'
						onClick={() => {
							trackButtonClick(ButtonClickTargets.LPDraftOutreachOpen);
							setDraftOutreachOpen(true);
						}}
					/>
				)}
			</Box>

			{/* KPIs */}
			<LP360KPIs accountId={accountId} />
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
					gap: '14px',
					gridAutoRows: '251px',
					alignItems: 'stretch',
				}}
			>
				{(loading || account) && (
					<LPProfileCard account={account} loading={loading} />
				)}
				<LPContactsCard
					contacts={contacts}
					loading={contactsLoading}
					loadingMore={contactsLoadingMore}
					hasMore={contactsHaveMore}
					error={contactsError}
					loadMore={loadMoreContacts}
				/>
			</Box>
			{(loading || account) && (
				<LPDescriptionCard
					description={account?.description}
					loading={loading}
				/>
			)}
			<LPFundraisingsCard accountId={accountId} />
			<InvestorCommitmentsCard accountId={accountId} />

			{/* Recent interactions (3/4) + Activity overview (1/4) */}
			{/* 458px ≈ 43px panel header + 7.5 interaction rows (≈55px each), so a
				half-cut row hints that the list scrolls/lazy-loads even though pages are 10 */}
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', md: '3fr 1fr' },
					gridAutoRows: '458px',
					gap: '14px',
				}}
			>
				<LPRecentInteractionsCard accountId={accountId} />
				<LPActivityOverviewCard accountId={accountId} />
			</Box>

			<LPHealthScores accountId={accountId} />
			{account && isDraftOutreachEnabled && (
				<DraftOutreachDrawer
					accountId={accountId}
					accountName={account.name}
					open={draftOutreachOpen}
					onClose={() => setDraftOutreachOpen(false)}
				/>
			)}
		</Box>
	);
};

export default LP360;
